import * as React from "react";
import { useParams } from "react-router-dom";
import ContactCard, { PersonData } from "../components/ContactCard/ContactCard";
import styles from "./QrLanding.module.css";

// ── GraphQL ──────────────────────────────────────────────────────────────────

const GRAPHQL_URL =
  process.env["REACT_APP_CONTACT_CARD_GRAPHQL_URL"] ?? "https://qr.acdmail.com/api/graphql";

const GET_CONTACT_CARD_QUERY = `
  query GetContactCard($code: String!) {
    person(qrCode: $code) {
      bizCardFirstName
      bizCardVCardFirstName
      bizCardLastName
      bizCardSuffix
      bizCardCompany
      bizCardTitle
      bizCardCellPhone
      bizCardDirectPhone
      bizCardOfficePhone
      bizCardOfficeExt
      bizCardEmail
      bizCardQRCodeId
      bizCardPhoto
    }
  }
`;

interface GraphQLResponse {
  data?: {
    person?: PersonData | null;
  };
  errors?: unknown[];
}

async function fetchContactCard(code: string): Promise<PersonData | null> {
  const response = await fetch(GRAPHQL_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query: GET_CONTACT_CARD_QUERY, variables: { code } }),
  });
  if (!response.ok) {
    return null;
  }
  const json: GraphQLResponse = await response.json();
  if (json.errors || !json.data || json.data.person == null) {
    return null;
  }
  return json.data.person;
}

// ── vCard generation ──────────────────────────────────────────────────────────

const COMPANY_DISPLAY: Record<string, string> = {
  ACD: "American Community Developers",
  SCC: "St. Clair Construction",
};

/**
 * Fold a vCard line per RFC 2425 §5.8.1:
 * lines longer than 75 octets are split with CRLF + a single SPACE.
 * The 75-octet limit applies to the encoded bytes, but since vCard 3.0
 * base64 data is ASCII, one character == one octet here.
 */
function foldLine(line: string): string {
  const MAX = 75;
  if (line.length <= MAX) return line;
  const chunks: string[] = [];
  chunks.push(line.slice(0, MAX));
  let offset = MAX;
  while (offset < line.length) {
    // continuation lines start with a space, so they get 74 data chars
    chunks.push(" " + line.slice(offset, offset + 74));
    offset += 74;
  }
  return chunks.join("\r\n");
}

/** Escape special characters per RFC 6350 and strip newlines. */
function vcfEscape(value: string): string {
  return value
    .replace(/\r?\n/g, " ")
    .replace(/\\/g, "\\\\")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

function isPresent(value: string | null | undefined): value is string {
  return value !== null && value !== undefined && value !== "";
}

function buildVCard(person: PersonData): string {
  const lines: string[] = [];

  lines.push("BEGIN:VCARD");
  lines.push("VERSION:3.0");

  const lastName = isPresent(person.bizCardLastName) ? person.bizCardLastName : "";
  const firstName = isPresent(person.bizCardVCardFirstName)
    ? person.bizCardVCardFirstName
    : isPresent(person.bizCardFirstName) ? person.bizCardFirstName : "";
  const suffix = isPresent(person.bizCardSuffix) ? person.bizCardSuffix : "";

  lines.push(`N:${vcfEscape(lastName)};${vcfEscape(firstName)};;;${vcfEscape(suffix)}`);

  const fn =
    [firstName, lastName].filter(Boolean).join(" ") + (suffix ? `, ${suffix}` : "");
  if (fn) {
    lines.push(`FN:${vcfEscape(fn)}`);
  }

  if (isPresent(person.bizCardCompany) && COMPANY_DISPLAY[person.bizCardCompany]) {
    lines.push(`ORG:${vcfEscape(COMPANY_DISPLAY[person.bizCardCompany]!)}`);
  }

  if (isPresent(person.bizCardTitle)) {
    lines.push(`TITLE:${vcfEscape(person.bizCardTitle)}`);
  }

  if (isPresent(person.bizCardCellPhone)) {
    lines.push(`TEL;TYPE=CELL,PREF:${person.bizCardCellPhone.replace(/\D/g, "")}`);
  }

  if (isPresent(person.bizCardDirectPhone)) {
    const pref = !isPresent(person.bizCardCellPhone) ? ",PREF" : "";
    lines.push(`TEL;TYPE=WORK,VOICE${pref}:${person.bizCardDirectPhone.replace(/\D/g, "")}`);
  }

  if (isPresent(person.bizCardOfficePhone)) {
    const pref = !isPresent(person.bizCardCellPhone) && !isPresent(person.bizCardDirectPhone) ? ",PREF" : "";
    const ext = isPresent(person.bizCardOfficeExt) ? `;ext=${person.bizCardOfficeExt}` : "";
    lines.push(`TEL;TYPE=WORK${pref}:${person.bizCardOfficePhone.replace(/\D/g, "")}${ext}`);
  }

  if (isPresent(person.bizCardEmail)) {
    lines.push(`EMAIL;TYPE=INTERNET:${vcfEscape(person.bizCardEmail)}`);
  }

  lines.push("ADR;TYPE=WORK:;;20250 Harper Ave.;Detroit;MI;48225;USA");

  if (isPresent(person.bizCardPhoto)) {
    // Strip the "data:image/jpeg;base64," prefix — keep only the raw base64 payload
    const base64 = person.bizCardPhoto.replace(/^data:[^;]+;base64,/, "");
    lines.push(foldLine(`PHOTO;ENCODING=b;TYPE=JPEG:${base64}`));
  }

  lines.push("END:VCARD");

  return lines.join("\r\n") + "\r\n";
}

function downloadVCard(person: PersonData): void {
  const vcf = buildVCard(person);
  console.log("Generated vCard:", vcf);
  const blob = new Blob([vcf], { type: "text/vcard;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const firstName = isPresent(person.bizCardVCardFirstName)
    ? person.bizCardVCardFirstName
    : isPresent(person.bizCardFirstName) ? person.bizCardFirstName : "contact";
  const lastName = isPresent(person.bizCardLastName) ? person.bizCardLastName : "";
  a.href = url;
  a.download = `${firstName}${lastName ? `-${lastName}` : ""}.vcf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Component ─────────────────────────────────────────────────────────────────

type LoadState =
  | { status: "loading" }
  | { status: "success"; person: PersonData }
  | { status: "error" };

const QrLanding: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const [loadState, setLoadState] = React.useState<LoadState>({ status: "loading" });

  React.useEffect(() => {
    if (!code) {
      setLoadState({ status: "error" });
      return;
    }
    let cancelled = false;
    fetchContactCard(code)
      .then((person) => {
        if (cancelled) return;
        if (person) {
          setLoadState({ status: "success", person });
        } else {
          setLoadState({ status: "error" });
        }
      })
      .catch(() => {
        if (!cancelled) setLoadState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  return (
    <div className={styles["page"]}>
      <div className={styles["container"]}>
        {loadState.status === "loading" && <div className={styles["spinner"]} />}

        {loadState.status === "error" && (
          <>
            <p className={styles["errorTitle"]}>Contact not found</p>
            <p className={styles["errorText"]}>
              This QR code does not match any contact in our system.
            </p>
          </>
        )}

        {loadState.status === "success" && (
          <>
            <ContactCard person={loadState.person} />
            <div className={styles["actions"]}>
              <button
                className={styles["addButton"]}
                onClick={() => downloadVCard(loadState.person)}
              >
                Add to Contacts
              </button>
              <a className={styles["homeLink"]} href="/">
                🏠 Back to Home
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default QrLanding;
